'use client';

import type { ReactNode } from 'react';

import { Group, Stack, Text, Title } from '@highlight-cp/core-ui';

export interface CardHeaderProps {
  /** Already translated — the heading the card or block is known by. */
  title: string;
  /** Rendered opposite the title: a switch, a disclosure toggle, a menu. */
  action?: ReactNode;
  description?: ReactNode;
  /** Put on the heading so a control group below it can point `aria-labelledby` at the title. */
  titleId?: string;
}

/**
 * ### CardHeader
 *
 * @description
 * The title, and optional description, a card opens with — one heading decision shared across the
 * cards that use it. The type runs a step below Mantine's headings, matching the design: 14px
 * semi-bold over a 12px description, where `h5` and `size="sm"` would give 16 over 14. It sets the
 * body font rather than the heading one — a card title reads as a label for what follows, not as a
 * step in the page's display hierarchy.
 *
 * The heading is always an `h4`, so a page that puts cards under anything other than an `h3` will
 * have a gap in its outline.
 *
 * @example
 * ```tsx
 * <CardHeader
 *   action={<Switch label={t('Rotate order for each tester')} />}
 *   description={t('Select products that testers will receive')}
 *   title={t('Product Assignments')}
 * />
 * ```
 *
 * @param props - {@link CardHeaderProps}
 * @returns React component
 */
export const CardHeader = ({ action, description, title, titleId }: CardHeaderProps) => (
  <Group align="flex-start" gap="lg" justify="space-between" wrap="nowrap">
    <Stack gap={2}>
      <Title ff="text" fw={600} id={titleId} order={4} size="h6">
        {title}
      </Title>
      {description && (
        <Text c="dimmed" size="xs">
          {description}
        </Text>
      )}
    </Stack>

    {action}
  </Group>
);
